import { useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useStore } from "../../js/state/store";
import { useResponsive } from "../../js/hooks/useResponsive";
import { useNotifications } from "../../js/hooks/useNotifications";
import { useHubUnread } from "../../js/hooks/useHubUnread";
import { useDesk } from "../../js/hooks/useDesk";
import { useContainedScroll } from "../../js/hooks/useContainedScroll";
import NavUnread from "../messaging/NavUnread";
import BrokerDeskSwitch from "../desk/BrokerDeskSwitch";
import { toTitleCase } from "../../js/utils/titleCase";
import { tourNavId } from "@tour/film.js";
import ResourcesLink from "./ResourcesLink";
import FrameReturnTop from "./FrameReturnTop";
import SuggestionWidget from "../suggest/SuggestionWidget";
import BetaFeedbackWidget from "../beta/BetaFeedbackWidget";
import { useAuth } from "../../js/hooks/useAuth";
import { canAccessFeature } from "../../js/auth/packageAccess";
import { manualsUrl, betaOnboardingUrl, demoVideoUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";

function gateSidebarLinks(links, session) {
  return links.filter((link) => !link.feature || canAccessFeature(session, link.feature));
}

function adminLinks(desk, session) {
  return [
    { to: desk.dashboard, label: "Dashboard", end: true },
    { to: desk.messages, label: "Messages", unread: "all" },
    { to: desk.suggestions, label: "Suggestions" },
    { to: "/admin/beta", label: "Beta Feedback" },
    { to: "/admin/hub/hall", label: "The Hub", unread: "hub" },
    { to: "/admin/hub", label: "Hub Review", end: true },
    { to: "/admin/founders", label: "Founder's View", end: true },
    { to: "/admin/prospects", label: "Prospects" },
    {
      href: betaOnboardingUrl(session),
      label: "Beta Onboarding Preview",
      external: true,
    },
    { to: desk.users, label: "Users" },
    { to: desk.accounts, label: "Account Management" },
    { to: desk.controls, label: "Controls" },
    { to: "/admin/sitemap", label: "Site Map", end: true },
    { href: demoVideoUrl(session), label: "Demo Video", external: true },
    { href: manualsUrl(undefined, session), label: "Role Manuals" },
    { to: `${desk.dashboard}#desk-previews`, label: "Desk Previews", hash: "#desk-previews" },
    { to: `${desk.dashboard}#sample-reports`, label: "Sample Reports", hash: "#sample-reports" },
    { to: desk.profile, label: "My Account" },
    { to: desk.settings, label: "Change Password" },
  ];
}

export default function Sidebar() {
  const { session } = useAuth();
  const { state, dispatch } = useStore();
  const { isDesktop } = useResponsive();
  const { hash, pathname } = useLocation();
  const { clientUnread, officeUnread, messageUnread } = useNotifications();
  const hubUnread = useHubUnread();
  const desk = useDesk();
  const bodyRef = useRef(null);
  const { showTop, returnToTop } = useContainedScroll(bodyRef);
  const open = isDesktop || state.ui.sidebarOpen;

  const links = gateSidebarLinks(
    desk.variant === "admin"
      ? adminLinks(desk, session)
      : desk.variant === "broker"
        ? [
            { to: desk.dashboard, label: "Dashboard", feature: "broker_desk" },
            { to: desk.clients, label: "Client Manager", feature: "client_manager" },
            { to: desk.prospects, label: "Prospects", feature: "prospects" },
            { to: desk.files, label: "Files", feature: "files" },
            { to: desk.documents, label: "Documents & Photos", feature: "documents" },
            { to: desk.reports, label: "Reports", feature: "reports" },
            { to: desk.responses, label: "Inquiry/Response", feature: "client_manager" },
            { to: desk.messages, label: "Client Messaging", unread: "client", end: true, feature: "client_messaging" },
            { to: desk.officeMessages, label: "Office Messaging", unread: "office", feature: "office_messaging" },
            { to: desk.calendar, label: "Calendar", feature: "calendar" },
            { to: `${desk.hub}/hall`, label: "The Hub", unread: "hub", feature: "hub" },
            { href: manualsUrl("broker", session), label: "User Guide" },
            { to: desk.profile, label: "Profile", feature: "broker_desk" },
            { to: desk.settings, label: "Settings", feature: "broker_desk" },
            { to: desk.archive, label: "Archive", feature: "archive" },
            { to: desk.audit, label: "Retention", feature: "retention" },
            { to: "/broker/controls/agents", label: "Agent Controls", feature: "agent_controls" },
          ]
        : [
            { to: desk.dashboard, label: "Dashboard", feature: "agent_desk" },
            { to: desk.clients, label: "Client Manager", feature: "client_manager" },
            { to: desk.prospects, label: "Prospects", feature: "prospects" },
            { to: desk.files, label: "Files", feature: "files" },
            { to: desk.documents, label: "Documents & Photos", feature: "documents" },
            { to: desk.reports, label: "Reports", feature: "reports" },
            { to: desk.responses, label: "Inquiry/Response", feature: "client_manager" },
            { to: desk.messages, label: "Client Messaging", unread: "client", feature: "client_messaging" },
            { to: desk.officeMessages, label: "Office Messaging", unread: "office", feature: "office_messaging" },
            { to: desk.calendar, label: "Calendar", feature: "calendar" },
            { to: `${desk.hub}/hall`, label: "The Hub", unread: "hub", feature: "hub" },
            { href: manualsUrl("agent", session), label: "User Guide" },
            { to: desk.settings, label: "Profile/Settings", feature: "agent_desk" },
          ],
    session
  );

  if (!open) return null;

  function unreadCount(kind) {
    if (kind === "office") return officeUnread;
    if (kind === "client") return clientUnread;
    if (kind === "all") return messageUnread;
    if (kind === "hub") return hubUnread;
    return 0;
  }

  function linkClass(link, isActive) {
    if (link.to === "/admin/accounts") {
      const onAccounts = pathname.startsWith("/admin/accounts");
      return `sidebar-link${onAccounts ? " is-active" : ""}`;
    }
    if (String(link.to || "").includes("/hub/hall")) {
      const root = link.to.replace(/\/hall$/, "");
      const onRooms = pathname.startsWith(`${root}/`) && pathname !== "/admin/hub";
      return `sidebar-link${onRooms ? " is-active" : ""}`;
    }
    if (link.hash) return `sidebar-link${hash === link.hash ? " is-active" : ""}`;
    if (link.end && hash && desk.variant === "admin") return "sidebar-link";
    return `sidebar-link${isActive ? " is-active" : ""}`;
  }

  return (
    <>
      {!isDesktop ? (
        <button
          type="button"
          className="sidebar-catch"
          aria-label="Close menu"
          onClick={() => dispatch({ type: "SET_SIDEBAR", payload: false })}
        />
      ) : null}
      <aside className={`sidebar sidebar-frame${isDesktop ? "" : " sidebar--drawer"}`} data-tour="desk-sidebar">
        {desk.variant === "broker" ? (
          <div className="sidebar-frame__chrome" data-tour="sidebar-chrome">
            <div className="sidebar-desk-switch">
              <BrokerDeskSwitch />
            </div>
          </div>
        ) : null}
        <div className="sidebar-frame__body" ref={bodyRef}>
          <nav className="sidebar-nav" data-tour="desk-nav" onClick={() => dispatch({ type: "SET_SIDEBAR", payload: false })}>
            {desk.variant !== "admin" ? (
                <NavLink
                to={desk.startHere}
                data-tour="sidebar-start"
                className={({ isActive }) => `sidebar-start-here${isActive ? " is-active" : ""}`}
              >
                New to REChecklist? Start Here.
              </NavLink>
            ) : null}
            {links.map((link) =>
              link.href ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="sidebar-link"
                  data-tour={tourNavId(link.label)}
                  {...JOURNEY_LINK_PROPS}
                >
                  {toTitleCase(link.label)}
                </a>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  data-tour={tourNavId(link.label)}
                  className={({ isActive }) => linkClass(link, isActive)}
                >
                  {toTitleCase(link.label)}
                  {link.unread ? <NavUnread count={unreadCount(link.unread)} /> : null}
                </NavLink>
              )
            )}
            <ResourcesLink className="sidebar-link resources-link" />
          </nav>
          {desk.variant !== "admin" ? <SuggestionWidget compact /> : null}
          {desk.variant !== "admin" ? <BetaFeedbackWidget compact /> : null}
        </div>
        <FrameReturnTop visible={showTop} onClick={returnToTop} />
      </aside>
    </>
  );
}
